import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {
  StepperLayout,
  useStepperState,
} from '../../../../../components/layouts/stepper/StepperLayout';
import DialogApiRequestStatus from '../../../../../components/dialogs/DialogApiRequestStatus';
import {
  DJMessageSelectionList,
  OnboardingMsgRequestReview,
  StingerMessageSelectionList,
} from './OnboardingMessageComponent';
import {IMessageOption} from '../../../../../dto/MessageOption.dto';
import {apiPost} from '../../../../../axiosConfig';
import AssignTaskButton from '../../../../../components/my-task/AssignTaskButton';
import {IMediaTrack} from '../../../../../dto/MediaTrack.dto';
import {
  getBaseMessageScripts,
  getNeptuneStingersScripts,
  getSchoolZoneStingerScripts,
  getStingersScripts,
} from './OnboardFileReader';
import {ITaskItem} from '../../../../../dto/Task.dto';

type Props = {
  defaultStep: number;
  taskData: ITaskItem;
  handleCancel: () => void;
  isGT: boolean;
  setIsGT: React.Dispatch<React.SetStateAction<boolean>>;
};
export type IMsgOptionOnBoard = IMessageOption & {
  selected?: boolean;
  message_option_id?: number | string | null;
  name: string;
  media_track?: IMediaTrack;
};
export type baseMsgScriptProps = {
  id: number | string | null;
  name: string;
  message: string;
  message_type: string;
  media_track?: IMediaTrack;
};
export type stringMsgScriptProps = {
  id: number | string | null;
  name: string;
  message: string[];
  message_type: string;
  isChoosable?: boolean;
  isSelected?: number;
  media_track?: IMediaTrack;
};
export type neptuneStringMsgScriptProps = {
  id: number | string | null;
  name: string;
  message: string;
  message_type: string;
  isSelected?: boolean;
  media_track?: IMediaTrack;
};
const OnboardingMessageRequest = ({
  defaultStep,
  taskData,
  handleCancel,
  isGT,
  setIsGT,
}: Props) => {
  const isSZ =
    taskData.company &&
    taskData.company.product &&
    taskData.company?.product.cmc_product === 'SchoolZone'
      ? true
      : false;

  const isNR =
    taskData.company &&
    taskData.company.product &&
    taskData.company?.product.cmc_product === 'Neptune Radio'
      ? true
      : false;
  const taskId = taskData?.id;
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>(0);
  const {activeStepIndex, onNextStep, onPreviousStep, onStepClick} =
    useStepperState(defaultStep);

  const [apiState, setApiState] = useState({status: '', error: null});

  const [selectedDjTracks, setSelectedDjTracks] = useState<IMediaTrack[]>([]);

  const navigate = useNavigate();
  const [stingersScripts, setStingersScripts] = useState<
    stringMsgScriptProps[]
  >([]);
  const [baseScripts, setBaseScripts] = useState<baseMsgScriptProps[]>([]);
  const [nrStingerScripts, setNrStingerScripts] = useState<
    neptuneStringMsgScriptProps[]
  >([]);

  useEffect(() => {
    if (taskData?.company && taskData.company.name) {
      if (isGT) {
        const scripts = getBaseMessageScripts(taskData?.company.name);
        const stScripts = getStingersScripts(taskData?.company.name);
        setBaseScripts(scripts);
        setStingersScripts(stScripts);
      } else if (taskData?.company?.product?.cmc_product === 'SchoolZone') {
        const szScripts = getSchoolZoneStingerScripts(taskData?.company?.name);
        setNrStingerScripts(szScripts);
      } else {
        setBaseScripts([
          {
            id: uuidv4(),
            name: '',
            message: '',
            message_type: 'dj',
          },
        ]);
        let partnerName = taskData?.company.name;
        let scriptName = taskData?.company?.product?.primary?.toString() || '';
        const stScripts = getNeptuneStingersScripts(partnerName, scriptName);
        if (stScripts && stScripts.length > 0) {
          setNrStingerScripts(stScripts);
        }
      }
    }
  }, [taskData, isGT]);

  const isValidDjMessage = baseScripts.every(
    val =>
      (val.name.trim().length > 0 && val.message.trim().length > 0) ||
      (val.name.trim().length === 0 && val.message.trim().length === 0),
  );

  const filteredData = nrStingerScripts.filter(
    item =>
      item.isSelected &&
      (item.name.trim() === '' || item.message.trim() === ''),
  );

  const isStingerMessageSelected = isGT
    ? stingersScripts.every(val => {
        let selectedMessage = '';
        if (
          val.message &&
          val.isSelected !== undefined &&
          val.message[val.isSelected] !== undefined
        ) {
          selectedMessage = val.message[val.isSelected]?.trim() || '';
        }
        return selectedMessage.length > 0 && val.name.trim().length > 0;
      })
    : filteredData?.length === 0;

  const steps = useMemo(() => {
    const stepList = [
      {
        label: 'Stinger message(s)',
        disabled: !isValidDjMessage,
      },
      {
        label: 'Review and create',
        disabled: !isStingerMessageSelected || isPreview,
      },
    ];

    if (!isSZ) {
      stepList.splice(0, 0, {
        label: 'DJ message(s)',
        disabled: false,
      });
    }

    return stepList;
  }, [isValidDjMessage, isSZ, isPreview, isStingerMessageSelected]);

  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null});
    let messages = [];
    if (isGT) {
      const newStingerscripts = stingersScripts
        .filter(
          st =>
            st.isSelected !== undefined &&
            st.message[st.isSelected] !== undefined,
        )
        .map(st => ({
          name: st.name,
          message: st.message[st.isSelected!],
          message_type: st.message_type,
          media_track: st.media_track,
        }));
      messages = [
        ...baseScripts.filter(
          val => val.name.trim().length > 0 && val.message.trim().length > 0,
        ),
        ...newStingerscripts,
      ];
    } else {
      const newStingerscripts = nrStingerScripts
        .filter(st => st.isSelected)
        .map(st => ({
          name: st.name,
          message: st.message,
          message_type: st.message_type,
          media_track: st.media_track,
        }));
      if (isSZ) {
        messages = [...newStingerscripts];
      } else {
        messages = [
          ...baseScripts.filter(
            val => val.name.trim().length > 0 && val.message.trim().length > 0,
          ),
          ...newStingerscripts,
        ];
      }
    }

    const fd = new FormData();
    let increament = 0;
    messages.forEach(item => {
      if (item.message) {
        fd.append(`messages[${increament}][message]`, item.message);
      }
      if (item.name) {
        fd.append(`messages[${increament}][name]`, item.name);
      }
      if (item.media_track && item.media_track.id) {
        fd.append(
          `messages[${increament}][media_track_id]`,
          item.media_track.id.toString(),
        );
      }
      if (item.message_type) {
        fd.append(`messages[${increament}][message_type]`, item.message_type);
      }

      increament++;
    });
    apiPost(`/v1/task/${taskId}/onboarding-message`, fd)
      .then(() => {
        setApiState({status: 'success', error: null});
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
    return 1;
  }, [taskId, baseScripts, stingersScripts, nrStingerScripts, isSZ, isGT]);

  // option to edit the form when api is failed
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  const handleClose = useCallback(() => {
    setApiState({status: '', error: null});
    navigate(0);
  }, [navigate]);

  useEffect(() => {
    if (selectedDjTracks.length && isNR) {
      const trackIds = selectedDjTracks.map(val => val.id);
      setBaseScripts(prev => {
        const previousSelectedTrackIds =
          prev
            .filter(val => val.media_track)
            ?.map(val => val?.media_track?.id ?? null) ?? [];
        const deletedTracks =
          previousSelectedTrackIds.filter(
            val => !trackIds.includes(Number(val)),
          ) ?? [];
        const createNewTrackMessage =
          selectedDjTracks
            .filter(val => !previousSelectedTrackIds.includes(Number(val.id)))
            .map(val => {
              return {
                id: uuidv4(),
                name: '',
                message: val.track_name,
                message_type: 'dj',
                media_track: val,
              };
            }) ?? [];
        return [
          ...createNewTrackMessage,
          ...prev.filter(
            val =>
              !deletedTracks.includes(val?.media_track?.id ?? 0) &&
              !(
                val.name.trim().length === 0 && val.message.trim().length === 0
              ),
          ),
        ];
      });
    }
  }, [isNR, selectedDjTracks]);

  return (
    <StepperLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onBack={onPreviousStep}
      onNext={onNextStep}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onStepClick={onStepClick}
      isSubmitted={apiState.status !== ''}>
      <AssignTaskButton
        isPreview={isPreview}
        setIsPreview={setIsPreview}
        setCompanyId={setCompanyId}
        setIsGT={setIsGT}
        task_id={taskId}
      />
      {activeStepIndex === 0 && !isSZ && (
        <DJMessageSelectionList
          baseScripts={baseScripts}
          setBaseScripts={setBaseScripts}
          selectedDjTracks={selectedDjTracks}
          setSelectedDjTracks={setSelectedDjTracks}
          companyId={companyId}
          isNR={isNR}
        />
      )}
      {((activeStepIndex === 0 && isSZ) ||
        (activeStepIndex === 1 && !isSZ)) && (
        <StingerMessageSelectionList
          stingersScripts={stingersScripts}
          setStingersScripts={setStingersScripts}
          nrStingerScripts={nrStingerScripts}
          setNrStingerScripts={setNrStingerScripts}
          isGT={isGT}
          companyId={companyId}
        />
      )}
      {((activeStepIndex === 1 && isSZ) ||
        (activeStepIndex === 2 && !isSZ)) && (
        <OnboardingMsgRequestReview
          djMessageOptions={baseScripts}
          stingerMessageOptions={stingersScripts}
          nrStingerScripts={nrStingerScripts}
          isGT={isGT}
          isSZ={isSZ}
        />
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </StepperLayout>
  );
};

export default OnboardingMessageRequest;
